import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
  Spinner,
} from '@apollo/orbit';
import React from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { DocsRouter } from 'src/lib/routers';

import { ErrorMessage } from '../errorMessage/ErrorMessage';

import { PublishYourSchema } from './PublishYourSchema';
import { PublishModeTab } from './shared';
import { usePublishedSchema } from './usePublishedSchema';

interface Props {
  onComplete?: () => void;
  __test?: {
    initialTab?: PublishModeTab;
    staging?: boolean;
  };
}

export const PublishYourSchemaCard = ({ onComplete, __test }: Props) => {
  const graphRef = useGraphRef();

  const { data, loading, error, architecture, published } = usePublishedSchema({
    graphId: graphRef?.graphId,
    graphVariant: graphRef?.graphVariant,
  });

  const federated = architecture === 'federation';

  return (
    <Card className="m-8 w-full max-w-3xl">
      {!loading && (
        <CardHeader>
          <CardTitle>
            {published ? 'Update schema' : 'Publish your schema'}
            {federated ? ' using Federation' : ''}
          </CardTitle>
          <CardDescription>
            {federated ? (
              <>
                To learn more about managed federation, visit the{' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  to={DocsRouter.path('ManagedFederation')}
                >
                  Apollo docs.
                </Link>
              </>
            ) : !published ? (
              'Pick one of these options. You can always change it later.'
            ) : undefined}
          </CardDescription>
        </CardHeader>
      )}
      <CardBody>
        {error ? (
          <ErrorMessage error={error} />
        ) : loading || !data?.graph ? (
          <div className="flex items-center justify-center">
            <Spinner />
          </div>
        ) : graphRef ? (
          <PublishYourSchema
            graph={data?.graph}
            graphId={graphRef.graphId}
            graphVariant={graphRef.graphVariant}
            federated={federated}
            onComplete={onComplete}
            __test={__test}
          />
        ) : (
          <NotFound
            title="Unexpected error"
            subtitle={
              <>
                We've sent you somewhere we shouldn't have! Please{' '}
                <ContactSupportLink>contact support</ContactSupportLink> and let
                us know.
              </>
            }
          />
        )}
      </CardBody>
    </Card>
  );
};
